import errors from './en/errors';
import { errorGroupOf } from './error-groups';
import { getLocale } from './store';
import { tr } from './translate';

type ErrorKey = keyof typeof errors.en;

/** Nest's placeholder when a thrown exception carried no message — says nothing. */
const EMPTY_SERVER_MESSAGES = new Set(['Request failed', 'Internal server error']);

/**
 * The text to show for a failed request in the UI language:
 *  1. the translation of the API error code (`code_<code>`, `errors` namespace);
 *  2. in English, the server's own message (it is English copy and the most specific);
 *  3. the translated text of the code's family (`*_not_found` → "This item no longer
 *     exists or you don't have access to it", see error-groups.ts);
 *  4. `fallback`, else the server's message in English, else "Action failed.".
 * A non-English UI never shows the server's English message.
 */
export function errorText(err: unknown, fallback?: string): string {
  if (err && typeof err === 'object') {
    if (err instanceof TypeError) return tr(errors, 'network');
    const code = (err as { code?: unknown }).code;
    const raw = (err as { message?: unknown }).message;
    const message = typeof raw === 'string' && raw && !EMPTY_SERVER_MESSAGES.has(raw) ? raw : null;
    const english = getLocale() === 'en';
    if (typeof code === 'string' && code) {
      const key = `code_${code}`;
      if (key in errors.en) return tr(errors, key as ErrorKey);
      if (english && message) return message;
      const group = errorGroupOf(code);
      if (group) return tr(errors, group);
    } else if (english && message) {
      return message;
    }
  }
  return fallback ?? tr(errors, 'actionFailed');
}
